import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from "@angular/core";
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { timeout } from "@app/class/Utilities";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { VAutocompleteComponent } from "@app/directives/_forms/v-autocomplete/v-autocomplete.component";
import { VDialogRef } from "@app/directives/_layout/v-dialog/v-dialog-ref";
import { DialogService } from "@app/directives/_layout/v-dialog/v-dialog.service";
import { IKeyValuePair } from "@app/interfaces/IKeyValuePair";
import { IVehicle } from "@app/interfaces/IVehicle";
import { DeviceComparisonStatus, DeviceParameterProfileMaterialisationOptions, DeviceParameterType, DeviceProfileService, IDeviceParameterProfile, IDeviceParameterProfileMapping } from "@app/services/device-profile.service";
import { EnumService } from "@app/services/enum.service";
import { TranslationService } from "@app/services/translation.service";
import { untilDestroyed } from "@ngneat/until-destroy";
import { copy } from "@uirouter/angularjs";
import './device-profile-editor.component.scss';

export interface IDisplayParameter {
    type: DeviceParameterType;
    value: number;
    enabled: boolean;
}

export interface IDisplayDeviceParameterProfileMapping {
    mapping: IDeviceParameterProfileMapping;
    vehicle: IVehicle;
}

@Component({
    selector: 'device-profile-editor',
    template: require('./device-profile-editor.component.html')
})
export class DeviceProfileEditorComponent implements AfterViewInit {

    @Input() vehicles: IVehicle[];
    @Input() deviceProfile: IDeviceParameterProfile;
    @Output() save = new EventEmitter<IDeviceParameterProfile>();
    @ViewChild("vehiclesAutocomplete", { static: false }) vehiclesAutocomplete: VAutocompleteComponent<IVehicle>;
    @ViewChild("badVehicleRemovalTemplate") badVehicleRemovalTemplate: TemplateRef<any>;

    dirtyDeviceProfile: IDeviceParameterProfile;

    displayParameters: IDisplayParameter[];
    displayDeviceParameterProfileMappings: IDisplayDeviceParameterProfileMapping[];
    mappingsRequiringRemoval: IDisplayDeviceParameterProfileMapping[];
    mappingRemovalDialog: VDialogRef;

    existingDeviceProfiles: IDeviceParameterProfile[];
    displayVehicles: IVehicle[];

    availableDeviceParameterTypes: IKeyValuePair<typeof DeviceParameterType>[];
    DeviceParameterType = DeviceParameterType;

    deviceProfileForm: FormGroup;
    mappingsInvalid: boolean;

    saving: boolean;
    savedChanges: boolean;

    hasAllResourcesAssociation: boolean;
    allResourcesAssociationError: string;

    cachedMappings: IDeviceParameterProfileMapping[];

    get formInvalid(): boolean {
        return this.mappingsInvalid || this.deviceProfileForm?.invalid || !!this.allResourcesAssociationError;
    }

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly formBuilder: FormBuilder,
        private readonly deviceProfileService: DeviceProfileService,
        private readonly translationService: TranslationService,
        private readonly dialogService: DialogService
    ) {
        this.setupParametersEnum();
    }

    ngAfterViewInit(): void {
        this.getData();
    }

    onDisplayParamaterToggled(displayParameter: IDisplayParameter): void {
        this.dirtyDeviceProfile.ParameterMap[displayParameter.type] = (displayParameter.enabled) ? 0 : null;

        this.refreshDisplayParameters();
    }

    onDisplayParameterValueChanged(displayParameter: IDisplayParameter, value: number): void {
        this.dirtyDeviceProfile.ParameterMap[displayParameter.type] = value;

        if (!value && value !== 0) {
            return;
        }

        this.refreshDisplayParameters();
    }

    onDisplayParameterInputBlur(displayParameter: IDisplayParameter): void {
        const currentValue = this.dirtyDeviceProfile.ParameterMap[displayParameter.type];

        if (!!currentValue || currentValue === 0) {
            return;
        }

        this.refreshDisplayParameters();
    }

    onVehicleSelected(vehicle: IVehicle): void {
        this.vehiclesAutocomplete.clearInput();

        if (this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings.find(mapping => mapping.VehicleId === vehicle.Id)) {
            return;
        }

        this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings.push({
            Id: 0,
            DeviceParameterProfileId: this.dirtyDeviceProfile.Id,
            VehicleId: vehicle.Id
        });

        this.refresh();
    }

    async saveDeviceProfile(silent: boolean = false): Promise<void> {

        this.saving = !silent;

        const savedProfile: IDeviceParameterProfile = await this.deviceProfileService.addUpdateProfile(this.dirtyDeviceProfile);

        this.savedChanges = !!savedProfile?.Id;

        const promise = (silent) ? Promise.resolve() : timeout(2000).then(() => this.save.emit(savedProfile));

        promise.finally(() => {
            this.saving = false;
        })
    }

    close(): void {

    }

    vehicleFilter = (vehicle: IVehicle, text: string): boolean => {
        return this.vehicleFilterSearchPredicate(vehicle, text);
    }

    vehicleFilterSearchPredicate(vehicle: IVehicle, text: string): boolean {
        const alreadyAdded: boolean = !!this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings?.find(mapping => mapping.VehicleId === vehicle.Id);
        const matchesName: boolean = vehicle.ShortReg.toLowerCase().includes(text.toLowerCase());
        const hasError: boolean = vehicle.deviceParameterComparisonResult?.hasAssignedProfile;

        return !hasError && !alreadyAdded && matchesName;
    }

    removeVehicleMappingById(vehicleId: number, refresh: boolean = true): void {
        const indexOfExistingMapping = this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings.findIndex(mapping => mapping.VehicleId === vehicleId);

        if (indexOfExistingMapping === -1) {
            return;
        }

        this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings.splice(indexOfExistingMapping, 1);

        if (!refresh) {
            return;
        }

        this.refresh();
    }

    onApplyToAllResourcesChanged(value: boolean): void {

        if (value) {
            this.cacheMappings();
            this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings = [this.createAllResourcesAssociation()];
        } else {
            this.recoverCachedMappings();
        }

        this.refresh();
    }

    onParameterTypeValueChanged(parameterType: DeviceParameterType, value: number): void {
        this.dirtyDeviceProfile.ParameterMap[DeviceParameterType[parameterType]] = value;

        Object.keys(this.deviceProfileForm.controls)?.forEach(key => {
            this.deviceProfileForm.get(key)?.updateValueAndValidity();
        });
    }

    onClickAcknowledgeRemoval(): void {
        this.mappingRemovalDialog?.close();
        this.mappingsRequiringRemoval = [];
    }

    //--

    private setupParametersEnum(): void {
        const parameterTypesArray: IKeyValuePair<typeof DeviceParameterType>[] = EnumService.enumToArray("DeviceParameterType", DeviceParameterType);

        this.availableDeviceParameterTypes = [
            parameterTypesArray.find(item => +item.value === DeviceParameterType.OverRev),
            parameterTypesArray.find(item => +item.value === DeviceParameterType.GreenBandUpper),
            parameterTypesArray.find(item => +item.value === DeviceParameterType.SweetSpotUpper),
            parameterTypesArray.find(item => +item.value === DeviceParameterType.SweetSpotLower),
            parameterTypesArray.find(item => +item.value === DeviceParameterType.GreenBandLower)
        ];
    }


    private refresh(): void {
        this.validateMappings(this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings);
        this.refreshDisplayMappings();
        this.refreshDisplayVehicles();
    }

    private getData(): void {
        this.mappingsRequiringRemoval = [];


        this.deviceProfileService.getProfiles({ Active: true, MaterialisationOptions: DeviceParameterProfileMaterialisationOptions.WithMappings }).pipe(untilDestroyed(this)).subscribe({
            next: deviceProfiles => {

                this.existingDeviceProfiles = deviceProfiles;
                this.prepareModel();
                this.determineAllResourcesAssociation();
                this.buildForm();
            }
        })
        
    }

    private refreshDisplayVehicles(): void {
        if (!this.vehicles?.length) {
            return;
        }

        this.displayVehicles = this.vehicles.filter(vehicle => {

            const vehicleDeviceComparisonStatus: DeviceComparisonStatus = this.deviceProfileService.getDeviceParameterComparisonResultStatus(vehicle?.deviceParameterComparisonResult);
            const incompatibleUnit = vehicleDeviceComparisonStatus === DeviceComparisonStatus.IncompatibleUnit || vehicleDeviceComparisonStatus === DeviceComparisonStatus.NoUnitInstalled;

            const matchingDeviceProfileMapping = this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings?.find(mapping => mapping.VehicleId === vehicle.Id);
            const hasAssignedProfile = vehicle?.deviceParameterComparisonResult?.hasAssignedProfile;

            const existingMatchingMapping = this.existingDeviceProfiles.find(existingDeviceProfile => {
                return existingDeviceProfile.Id !== this.deviceProfile.Id
                    && this.mappingsContainVehicleId(existingDeviceProfile.UnitDeviceParameterProfileMappings, vehicle.Id);
            });

            return !existingMatchingMapping && !hasAssignedProfile && !incompatibleUnit && !matchingDeviceProfileMapping;
        });
    }

    private buildForm(): void {
        this.deviceProfileForm = this.formBuilder.group({
            Name: [this.dirtyDeviceProfile.Name ?? "", [
                Validators.required,
                Validators.minLength(3),
                Validators.maxLength(100),
                CustomFormValidators.emptyWhiteSpace,
                this.deviceProfileService.uniqueDeviceProfileNameValidator(this.dirtyDeviceProfile, this.existingDeviceProfiles)
            ]]
        });

        this.availableDeviceParameterTypes.forEach(deviceParameterType => {
            this.deviceProfileForm.addControl(deviceParameterType.name, new FormControl(this.dirtyDeviceProfile.ParameterMap[deviceParameterType.name], [
                Validators.required,
                Validators.min(0),
                Validators.max(20000),
                CustomFormValidators.number,
                this.validateFormParameter(deviceParameterType)
            ]))
        })

        this.cd.detectChanges();
    }

    private validateFormParameter = (deviceParameterType: IKeyValuePair<typeof DeviceParameterType>) => (control: AbstractControl): ValidationErrors | null => {
        if (!control.value && control?.value !== 0) {
            return null;
        }

        const parameterIndex = this.availableDeviceParameterTypes.findIndex(availableParamType => +availableParamType.value === +deviceParameterType.value);
        const parameterAbove: IKeyValuePair<typeof DeviceParameterType> = this.availableDeviceParameterTypes[parameterIndex + 1];

        if (parameterIndex === 0) {
            if (control.value <= this.dirtyDeviceProfile.ParameterMap[parameterAbove.name]) {
                return { "customMessage": this.translationService.t("DeviceProfileEditor.ParameterValidationWarningTooLow") }
            }

            return null;
        }

        const parameterBelow: IKeyValuePair<typeof DeviceParameterType> = this.availableDeviceParameterTypes[parameterIndex - 1];

        if (parameterIndex === this.availableDeviceParameterTypes?.length - 1) {
            if (control.value >= this.dirtyDeviceProfile.ParameterMap[parameterBelow.name]) {
                return { "customMessage": this.translationService.t("DeviceProfileEditor.ParameterValidationWarningTooHigh") }
            }

            return null;
        }

        if (control.value <= this.dirtyDeviceProfile.ParameterMap[parameterAbove.name]) {
            return { "customMessage": this.translationService.t("DeviceProfileEditor.ParameterValidationWarningTooLow") }
        }

        if (control.value >= this.dirtyDeviceProfile.ParameterMap[parameterBelow.name]) {
            return { "customMessage": this.translationService.t("DeviceProfileEditor.ParameterValidationWarningTooHigh") }
        }

        return null;
    }

    private prepareModel(): void {

        this.dirtyDeviceProfile = (!this.deviceProfile?.Id)
            ? this.deviceProfileService.createDeviceParameterProfileTemplate()
            : copy(this.deviceProfile);

        if (this.deviceProfile.ParameterMap) {
            this.dirtyDeviceProfile.ParameterMap = copy(this.deviceProfile.ParameterMap);
        }

        if (this.deviceProfile.UnitDeviceParameterProfileMappings?.length) {
            this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings = [...this.deviceProfile.UnitDeviceParameterProfileMappings];
        }

        this.cacheMappings();

        this.refreshDisplayParameters();
        this.refresh();
    }

    private refreshDisplayParameters(): void {
        this.displayParameters = (this.displayParameters?.length) ? this.displayParameters : [];

        if (!this.dirtyDeviceProfile?.ParameterMap) {
            return;
        }

        Object.keys(this.dirtyDeviceProfile.ParameterMap).forEach((key, index) => {

            if (!DeviceParameterType[key]) {
                return;
            }

            const existingDisplayParameter = this.displayParameters.find(displayParameter => displayParameter.type === +key);
            const value: number = this.dirtyDeviceProfile.ParameterMap[key];
            const enabled: boolean = this.dirtyDeviceProfile.ParameterMap[key] === 0 || this.dirtyDeviceProfile.ParameterMap[key] > 0;

            if (existingDisplayParameter) {
                existingDisplayParameter.value = value;
                existingDisplayParameter.enabled = enabled;
                return;
            }

            this.displayParameters.push({
                type: +key,
                value: value,
                enabled: enabled
            });
        });
    }

    private refreshDisplayMappings(): void {

        const hasMappingsRequiringRemoval: boolean = !!this.mappingsRequiringRemoval?.length;

        this.displayDeviceParameterProfileMappings = [];

        if (!this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings?.length) {
            return;
        }

        this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings.forEach(mapping => {
            const matchingVehicle = this.vehicles.find(vehicle => vehicle.Id === mapping.VehicleId);

            if (!matchingVehicle) {
                return;
            }

            if (mapping.validationError === this.translationService.t("DeviceProfileService.IncompatibleUnitTooltip") && !hasMappingsRequiringRemoval) {
                this.mappingsRequiringRemoval.push({
                    vehicle: matchingVehicle,
                    mapping: mapping
                });
            }

            const newDisplayMapping: IDisplayDeviceParameterProfileMapping = {
                vehicle: matchingVehicle,
                mapping: mapping
            };

            this.displayDeviceParameterProfileMappings.push(newDisplayMapping);
        });

        if (this.mappingsRequiringRemoval.length && !hasMappingsRequiringRemoval) {
            this.mappingRemovalDialog = this.dialogService.open(this.badVehicleRemovalTemplate, null);
            this.removeMappingsAndSave(this.mappingsRequiringRemoval);
        }
    }

    private removeMappingsAndSave(mappings: IDisplayDeviceParameterProfileMapping[]): Promise<void> {
        if (!mappings?.length) {
            return;
        }

        mappings.forEach((mapping, index) => this.removeVehicleMappingById(mapping.vehicle.Id, index === mappings.length - 1));

        return this.saveDeviceProfile(true);
    }

    private determineAllResourcesAssociation(): void {
        this.hasAllResourcesAssociation = false;

        if (!this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings?.length) {
            return;
        }

        const allResourcesAssociation = this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings.find(association => !association.VehicleId);

        if (!allResourcesAssociation) {
            return;
        }

        this.hasAllResourcesAssociation = true;
    }

    private createAllResourcesAssociation(): IDeviceParameterProfileMapping {
        const newAssociation: IDeviceParameterProfileMapping = {
            Id: 0,
            VehicleId: null,
            DeviceParameterProfileId: 0
        };

        return newAssociation;
    }

    private validateMappings(mappings: IDeviceParameterProfileMapping[]): void {
        this.mappingsInvalid = false;
        this.allResourcesAssociationError = null;

        if (!mappings?.length) {
            return;
        }

        this.determineAllResourcesAssociation();

        mappings.forEach(mapping => this.validateMapping(mapping));
    }

    private validateMapping(mapping: IDeviceParameterProfileMapping): void {

        const vehicleId: number = mapping.VehicleId;

        if (this.hasAllResourcesAssociation) {
            this.validateAllResourceAssociation(mapping);
            return;
        }

        const vehicle = this.vehicles.find(vehicle => vehicle.Id === mapping.VehicleId);
        const vehicleDeviceComparisonStatus: DeviceComparisonStatus = this.deviceProfileService.getDeviceParameterComparisonResultStatus(vehicle?.deviceParameterComparisonResult);

        if (vehicleDeviceComparisonStatus === DeviceComparisonStatus.IncompatibleUnit
            || vehicleDeviceComparisonStatus === DeviceComparisonStatus.NoUnitInstalled) {
            mapping.validationError = this.translationService.t("DeviceProfileService.IncompatibleUnitTooltip");

            this.mappingsInvalid = true;
        }

        const existingMatchingMapping = this.existingDeviceProfiles.find(existingDeviceProfile => {
            return existingDeviceProfile.Id !== this.deviceProfile.Id
                && this.mappingsContainVehicleId(existingDeviceProfile.UnitDeviceParameterProfileMappings, vehicleId)
        });

        if (existingMatchingMapping) {
            const profileName = this.existingDeviceProfiles.find(existingDeviceProfile => existingDeviceProfile.Id === existingMatchingMapping.Id)?.Name ?? "Unknown";

            mapping.validationError = this.translationService.t("DeviceProfileEditor.AlreadyAssignedVehicleWarning", profileName);

            this.mappingsInvalid = true;
        }
    }

    private validateAllResourceAssociation(mapping: IDeviceParameterProfileMapping): void {
        const existingMatchingMapping = this.existingDeviceProfiles.find(existingDeviceProfile => {
            return existingDeviceProfile.Id !== this.deviceProfile.Id
                && this.mappingsContainsAllResourcesMapping(existingDeviceProfile.UnitDeviceParameterProfileMappings)
        });

        if (existingMatchingMapping) {
            const profileName = this.existingDeviceProfiles.find(existingDeviceProfile => existingDeviceProfile.Id === existingMatchingMapping.Id)?.Name ?? "Unknown";

            this.allResourcesAssociationError = this.translationService.t("DeviceProfileEditor.AlreadyAssignedAllWarning", profileName);
        }
    }

    private mappingsContainsAllResourcesMapping(mappings: IDeviceParameterProfileMapping[]): boolean {
        if (!mappings?.length) {
            return false;
        }

        for (const mapping of mappings) {
            if (!mapping.VehicleId) {
                return true;
            }
        }

        return false;
    }

    private mappingsContainVehicleId(mappings: IDeviceParameterProfileMapping[], vehicleId: number): boolean {
        if (!mappings?.length || !vehicleId) {
            return false;
        }

        for (const mapping of mappings) {
            if (mapping.VehicleId === vehicleId) {
                return true;
            }
        }

        return false;
    }

    private cacheMappings(): void {
        this.determineAllResourcesAssociation();

        if (this.hasAllResourcesAssociation) {
            this.cachedMappings = [];
            return;
        }

        this.cachedMappings = [...this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings];
    }

    private recoverCachedMappings(): void {
        this.dirtyDeviceProfile.UnitDeviceParameterProfileMappings = [...this.cachedMappings];
    }
}
